import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { Formik, FormikConfig, FormikProps } from 'formik';
import React from 'react';

const FormContext = createContext<FormProps<any>>(null);

type Props<Values extends Record<string, any>> = FormContextProps &
    Omit<
        FormikConfig<Values>,
        'children' | 'validateOnChange' | 'validateOnBlur'
    > & {
        children?: ReactNode | ((props: FormProps<Values>) => ReactNode);
    };

interface FormContextProps {
    /** Delays writing field values to Formik state until blur or Enter key, reducing validation frequency for expensive schemas. */
    updateOnBlur?: boolean;
    /** Prevents validateOnChange and validateOnBlur from activating after the first submit. */
    disableValidationOnChange?: boolean;
}

// This provider is needed because Formik doesn't expose the validation schema in any hook.
// This is a known feature request and will be fixed in v3: https://github.com/jaredpalmer/formik/pull/3231
const HBFormProvider = <Values extends Record<string, any>>({
    children,
    validationSchema,
    updateOnBlur,
    disableValidationOnChange,
    ...props
}: Props<Values>) => {
    const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);

    // validationSchema.describe() is a very heavy call so this is all to reduce the calls to it to the absolute minimum
    const schemaRef = useRef(null);
    const schemaDescribedRef = useRef(null);
    if (validationSchema && schemaRef.current !== validationSchema) {
        schemaRef.current = validationSchema;
        schemaDescribedRef.current = validationSchema.describe();
    }

    return (
        <Formik
            {...props}
            validationSchema={validationSchema}
            validateOnBlur={hasSubmittedOnce && !disableValidationOnChange}
            validateOnChange={hasSubmittedOnce && !disableValidationOnChange}
        >
            {(formProps: FormProps<Values>) => {
                formProps.validationSchema = schemaDescribedRef.current;
                formProps.updateOnBlur = updateOnBlur;
                if (formProps.submitCount > 0) {
                    setHasSubmittedOnce(true);
                }
                return (
                    <FormContext.Provider value={formProps} {...props}>
                        {typeof children === 'function'
                            ? children(formProps)
                            : children}
                    </FormContext.Provider>
                );
            }}
        </Formik>
    );
};
export default HBFormProvider;

export const useFormContext = <T extends object>(): FormProps<T> =>
    useContext(FormContext);

export type TValidationTestParams = {
    max?: number;
    min?: number;
    [key: string]: any;
};

type FormProps<Values extends Record<string, any>> = FormikProps<Values> &
    FormContextProps & {
        validationSchema: {
            fields: Record<
                string,
                {
                    type: 'string' | 'number' | 'boolean';
                    optional?: boolean;
                    tests: {
                        name: string;
                        params: TValidationTestParams;
                    }[];
                }
            >;
        };
    };
